import { timezones } from "../timezones";
const fieldKeys = [
    'date',
    'month',
    'year',
    'minutes',
    'seconds',
    'microseconds',
    'timezone',
    'milliseconds',
];
export class PhpDate {
    constructor(date, month, year, hours, minutes, seconds, microseconds, timezone) {
        this.date = date;
        this.month = month;
        this.year = year;
        this.hours = hours;
        this.minutes = minutes;
        this.seconds = seconds;
        this.microseconds = microseconds;
        this.timezone = timezone;
        this._date = undefined;
        this.prevDate = undefined;
        this._month = undefined;
        this.prevMonth = undefined;
        this._year = undefined;
        this.prevYear = undefined;
        this._hours = undefined;
        this.prevHours = undefined;
        this._minutes = undefined;
        this.prevMinutes = undefined;
        this._seconds = undefined;
        this.prevSeconds = undefined;
        this._microseconds = undefined;
        this.prevMicroseconds = undefined;
        this._timezone = undefined;
        this.prevTimezone = undefined;
    }
    clone() {
        const cloned = new PhpDate(this.date, this.month, this.year, this.hours, this.minutes, this.seconds, this.microseconds, this.timezone);
        // Copy over private fields
        cloned._date = this._date;
        cloned.prevDate = this.prevDate;
        cloned._month = this._month;
        cloned.prevMonth = this.prevMonth;
        cloned._year = this._year;
        cloned.prevYear = this.prevYear;
        cloned._hours = this._hours;
        cloned.prevHours = this.prevHours;
        cloned._minutes = this._minutes;
        cloned.prevMinutes = this.prevMinutes;
        cloned._seconds = this._seconds;
        cloned.prevSeconds = this.prevSeconds;
        cloned._microseconds = this._microseconds;
        cloned.prevMicroseconds = this.prevMicroseconds;
        cloned._timezone = this._timezone;
        cloned.prevTimezone = this.prevTimezone;
        return cloned;
    }
    static createFromLocalDate(d = new Date()) {
        return new PhpDate(String(d.getDate()), String(d.getMonth() + 1), String(d.getFullYear()), String(d.getHours()).padStart(2, '0'), String(d.getMinutes()).padStart(2, '0'), String(d.getSeconds()).padStart(2, '0'), String(d.getMilliseconds() * 1000).padStart(6, '0'), Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
    static createFromUTCDate(d = new Date()) {
        return new PhpDate(String(d.getUTCDate()), String(d.getUTCMonth() + 1), String(d.getUTCFullYear()), String(d.getUTCHours()).padStart(2, '0'), String(d.getUTCMinutes()).padStart(2, '0'), String(d.getUTCSeconds()).padStart(2, '0'), String(d.getUTCMilliseconds() * 1000).padStart(6, '0'), 'UTC');
    }
    static createFromJson(input) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        return new PhpDate((_a = input.date) !== null && _a !== void 0 ? _a : '', (_b = input.month) !== null && _b !== void 0 ? _b : '', (_c = input.year) !== null && _c !== void 0 ? _c : '', (_d = input.hours) !== null && _d !== void 0 ? _d : '', (_e = input.minutes) !== null && _e !== void 0 ? _e : '', (_f = input.seconds) !== null && _f !== void 0 ? _f : '', (_h = (_g = input.microseconds) !== null && _g !== void 0 ? _g : (input.milliseconds * 1000)) !== null && _h !== void 0 ? _h : '', (_j = input.timezone) !== null && _j !== void 0 ? _j : undefined);
    }
    toJSON() {
        const res = {};
        for (let key of fieldKeys) {
            const value = this[key];
            if (value !== undefined) {
                res[key] = value;
            }
        }
        return res;
    }
    resolveNumberValue(resolvedName, prevName, currentName, minimum, maximum) {
        if (this[resolvedName] === undefined || this[prevName] !== this[currentName]) {
            const valueAsNumber = parseInt(String(this[currentName]), 10);
            if (valueAsNumber >= minimum && valueAsNumber <= maximum) {
                this[resolvedName] = valueAsNumber;
            }
            else {
                this[resolvedName] = null;
            }
            this[prevName] = this[currentName];
        }
        return this[resolvedName];
    }
    get resolvedTimezone() {
        var _a;
        if (this._timezone === undefined || this.prevTimezone !== this.timezone) {
            const timezone = (_a = this.timezone) === null || _a === void 0 ? void 0 : _a.toLowerCase();
            if (timezone === null) {
                return null;
            }
            this._timezone = timezones.find((value) => value.timezone_id.toLowerCase() === timezone);
            this.prevTimezone = this.timezone;
        }
        return this._timezone;
    }
    get resolvedYear() {
        return this.resolveNumberValue('_year', 'prevYear', 'year', 1000, 9999);
    }
    get resolvedWeekYear() {
        const tempDate = this.toLocalDate();
        if (isNaN(tempDate.getTime())) {
            return null;
        }
        tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
        return tempDate.getFullYear();
    }
    get resolvedMonth() {
        return this.resolveNumberValue('_month', 'prevMonth', 'month', 1, 12);
    }
    get resolvedMaxDaysInMonth() {
        const month = this.resolvedMonth;
        const year = this.resolvedYear;
        if (month === null) {
            return 31;
        }
        if (month === 2) {
            if (year === null || ((year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0))) {
                return 29;
            }
            return 28;
        }
        if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
            return 31;
        }
        return 30;
    }
    get resolvedDate() {
        let daysInMonth = this.resolvedMaxDaysInMonth;
        return this.resolveNumberValue('_date', 'prevDate', 'date', 1, daysInMonth);
    }
    get resolvedDayOfWeek() {
        if (this.resolvedYear && this.resolvedMonth, this.resolvedDate) {
            const d = new Date(this.resolvedYear, this.resolvedMonth, this.resolvedDate);
            return d.getDay();
        }
        return null;
    }
    get resolvedHours() {
        return this.resolveNumberValue('_hours', 'prevHours', 'hours', 0, 23);
    }
    get resolvedMinutes() {
        return this.resolveNumberValue('_minutes', 'prevMinutes', 'minutes', 0, 59);
    }
    get resolvedSeconds() {
        return this.resolveNumberValue('_seconds', 'prevSeconds', 'seconds', 0, 59);
    }
    get resolvedMicroseconds() {
        return this.resolveNumberValue('_microseconds', 'prevMicroseconds', 'microseconds', 0, 999999);
    }
    get milliseconds() {
        const micro = this.resolvedMicroseconds;
        if (micro === null) {
            return '';
        }
        return String(Math.floor(micro / 1000)).padStart(3, '0');
    }
    set milliseconds(val) {
        this.microseconds = val.length ? (val + '000') : '';
    }
    get resolvedMilliseconds() {
        const micro = this.resolvedMicroseconds;
        if (micro === null) {
            return null;
        }
        return Math.floor(micro / 1000);
    }
    get resolvedDaySuffix() {
        if (this.resolvedDate === null) {
            return null;
        }
        switch (this.resolvedDate) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
        ;
    }
    get resolvedDayOfYear() {
        const year = this.resolvedYear;
        if (year === null) {
            return null;
        }
        const startOfYear = new Date(Number(year), 0, 1);
        const diff = this.toLocalDate().getTime() - startOfYear.getTime();
        const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
        return dayOfYear;
    }
    get resolvedWeekNumber() {
        const tempDate = new Date(this.toLocalDate(new Date('unknown')).getTime());
        tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
        if (isNaN(tempDate.getTime())) {
            return null;
        }
        const firstThursday = new Date(tempDate.getFullYear(), 0, 4);
        firstThursday.setDate(firstThursday.getDate() + 3 - (firstThursday.getDay() + 6) % 7);
        const weekNumber = Math.floor((tempDate.getTime() - firstThursday.getTime()) / (1000 * 60 * 60 * 24 * 7)) + 1;
        return weekNumber;
    }
    get resolvedSwatchTime() {
        const date = this.toUtcDate();
        if (isNaN(date.getTime())) {
            return null;
        }
        // Convert the current date to UTC
        const utcHours = date.getUTCHours();
        const utcMinutes = date.getUTCMinutes();
        const utcSeconds = date.getUTCSeconds();
        // Calculate the total seconds since midnight UTC
        const totalSeconds = utcHours * 3600 + utcMinutes * 60 + utcSeconds;
        // Adjust for BMT (UTC+1)
        const bmtSeconds = totalSeconds + 3600;
        // Calculate the Swatch beats
        const swatchBeats = Math.floor((bmtSeconds / 86.4) % 1000);
        return swatchBeats;
    }
    get resolvedIsDst() {
        if (this.resolvedTimezone && !this.resolvedTimezone.dst) {
            return false;
        }
        const date = this.toLocalDate();
        const jan = new Date(date.getFullYear(), 0, 1); // January 1st (likely non-DST)
        const jul = new Date(date.getFullYear(), 6, 1); // July 1st (likely DST)
        const standardTimezoneOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
        // Compare current date's offset with the standard offset
        return date.getTimezoneOffset() < standardTimezoneOffset;
    }
    get resolvedUnixTimestamp() {
        const date = this.toLocalDate();
        const time = date.getTime();
        return isNaN(time) ? null : time;
    }
    toLocalDate(currentTime = new Date()) {
        var _a, _b, _c, _d, _e, _f;
        const returnValue = new Date();
        returnValue.setFullYear((_a = this.resolvedYear) !== null && _a !== void 0 ? _a : currentTime.getFullYear(), ((_b = this.resolvedMonth) !== null && _b !== void 0 ? _b : currentTime.getMonth()) - 1, (_c = this.resolvedDate) !== null && _c !== void 0 ? _c : currentTime.getDate());
        returnValue.setHours((_d = this.resolvedHours) !== null && _d !== void 0 ? _d : currentTime.getHours(), (_e = this.resolvedMinutes) !== null && _e !== void 0 ? _e : currentTime.getMinutes(), (_f = this.resolvedSeconds) !== null && _f !== void 0 ? _f : currentTime.getSeconds(), this.resolvedMicroseconds ? (this.resolvedMicroseconds / 1000) : 0);
        return returnValue;
    }
    toUtcDate(currentTime = new Date()) {
        var _a, _b, _c, _d, _e, _f;
        const returnValue = new Date();
        returnValue.setFullYear((_a = this.resolvedYear) !== null && _a !== void 0 ? _a : currentTime.getFullYear(), ((_b = this.resolvedMonth) !== null && _b !== void 0 ? _b : currentTime.getMonth()) - 1, (_c = this.resolvedDate) !== null && _c !== void 0 ? _c : currentTime.getDate());
        returnValue.setUTCHours((_d = this.resolvedHours) !== null && _d !== void 0 ? _d : currentTime.getHours(), (_e = this.resolvedMinutes) !== null && _e !== void 0 ? _e : currentTime.getMinutes(), (_f = this.resolvedSeconds) !== null && _f !== void 0 ? _f : currentTime.getSeconds(), this.resolvedMicroseconds ? (this.resolvedMicroseconds / 1000) : 0);
        const timezone = this.resolvedTimezone;
        if (timezone) {
            returnValue.setSeconds(returnValue.getSeconds() + timezone.offset);
        }
        return returnValue;
    }
}
//# sourceMappingURL=PhpDate.js.map
